import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DotProps, TooltipProps } from 'recharts';
import type { MetricItemDTO } from '@/types/agentEvents';
import type { MetricsBarChartProps } from './MetricsBarChart';
import { formatSecondsToHHMMSS } from '@/utils/metricsAggregate';

function formatIntervalAxisLabel(intervalStartTime: string): string {
  const d = new Date(intervalStartTime);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getHours() !== 0 || d.getMinutes() !== 0
      ? { hour: '2-digit', minute: '2-digit' }
      : {}),
  });
}

const defaultValueToNumber = (value: number | string): number => {
  if (typeof value === 'number') return value;
  const n = parseFloat(String(value).replace(/%/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const LINE_STROKE = '#2563eb';
const DOT_FILL = '#2563eb';

type IntervalTooltipPayload = {
  label: string;
  tooltipValue: string;
};

function IntervalTooltip({
  active,
  payload,
  label,
}: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as IntervalTooltipPayload;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)',
      }}
    >
      <div style={{ color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{row.tooltipValue}</div>
    </div>
  );
}

type LinePointPayload = {
  fill: string;
  tooltipValue: string;
  label: string;
  value: number;
  id: string;
};

function renderLineDot(props: DotProps & { payload?: LinePointPayload }) {
  const { cx, cy, payload } = props;
  const fill = payload?.fill ?? DOT_FILL;
  if (cx == null || cy == null) return <g />;
  return (
    <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#fff" strokeWidth={2} />
  );
}

function renderActiveDot(props: DotProps & { payload?: LinePointPayload }) {
  const { cx, cy, payload } = props;
  const fill = payload?.fill ?? DOT_FILL;
  if (cx == null || cy == null) return <g />;
  return (
    <circle cx={cx} cy={cy} r={7} fill={fill} stroke="#fff" strokeWidth={2} />
  );
}

/**
 * Line chart for interval metric rows where Y is a numeric scale (e.g. duration in seconds).
 * Expects {@link valueToNumber} to map API values (e.g. HH:MM:SS) to seconds for the axis.
 */
export function MetricIntervalNumericLineChart({
  data,
  valueToNumber = defaultValueToNumber,
  valueFormatter = v => String(v),
  height = 280,
}: MetricsBarChartProps) {
  const chartPoints = useMemo(() => {
    const sorted = [...data].sort(
      (a, b) =>
        new Date(a.intervalStartTime ?? 0).getTime() -
        new Date(b.intervalStartTime ?? 0).getTime()
    );
    return sorted
      .filter((item): item is MetricItemDTO & { intervalStartTime: string } =>
        Boolean(item.intervalStartTime)
      )
      .map(item => {
        const num = valueToNumber(item.value);
        return {
          id: item.id,
          label: formatIntervalAxisLabel(item.intervalStartTime),
          value: Number.isFinite(num) ? num : 0,
          fill: DOT_FILL,
          tooltipValue: valueFormatter(item.value),
        };
      });
  }, [data, valueToNumber, valueFormatter]);

  const yMax = useMemo(() => {
    const values = chartPoints.map(p => p.value);
    const max = Math.max(0, ...values);
    return max <= 0 ? 1 : max * 1.12;
  }, [chartPoints]);

  if (chartPoints.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartPoints}
        margin={{ top: 8, right: 12, left: 8, bottom: 48 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#e2e8f0"
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11 }}
          interval={0}
          angle={chartPoints.length > 5 ? -35 : 0}
          textAnchor={chartPoints.length > 5 ? 'end' : 'middle'}
          height={chartPoints.length > 5 ? 56 : 32}
        />
        <YAxis
          domain={[0, yMax]}
          tickFormatter={v => formatSecondsToHHMMSS(Math.max(0, Math.floor(v)))}
          width={72}
          tick={{ fontSize: 10 }}
        />
        <Tooltip content={<IntervalTooltip />} />
        <Line
          type="monotone"
          dataKey="value"
          stroke={LINE_STROKE}
          strokeWidth={2}
          dot={renderLineDot}
          activeDot={renderActiveDot}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
